import { useEffect, useMemo } from 'react';
import { AlertBadge } from './components/AlertBadge';
import { BrandHeader } from './components/BrandHeader';
import { DesktopBrandPanel } from './components/DesktopBrandPanel';
import { Layout } from './components/Layout';
import { PersonalizedMessage } from './components/PersonalizedMessage';
import { PrimaryCTA } from './components/PrimaryCTA';
import { SecondaryCTA } from './components/SecondaryCTA';
import { useDeeplinkLauncher } from './hooks/useDeeplinkLauncher';
import { useTrackingParams } from './hooks/useTrackingParams';
import { usePersonalization } from './hooks/usePersonalization';
import { trackEvent } from './lib/analytics';
import { reportLanding } from './lib/api';
import { APP_STORE_URL, PLAY_STORE_URL } from './lib/constants';

const DEFAULT_CTA_LABEL = '우리엔에서 바로 보기';

function App() {
  const { trackingId } = useTrackingParams();
  const personalization = usePersonalization(trackingId);

  const { platform, openApp, openInstall } = useDeeplinkLauncher();

  const currentUrl = useMemo(() => {
    if (typeof window === 'undefined') return 'https://m.woorien.com/';
    return window.location.href;
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    trackEvent('page_view', {
      trackingId,
      platform: platform.iOS
        ? 'ios'
        : platform.android
          ? 'android'
          : 'desktop',
      inAppBrowser: platform.inAppBrowser,
    });
    void reportLanding(trackingId, controller.signal);
    return () => controller.abort();
  }, [trackingId, platform]);

  const ctaLabel =
    personalization.status === 'success'
      ? personalization.data.ctaLabel || DEFAULT_CTA_LABEL
      : DEFAULT_CTA_LABEL;

  const handlePrimary = () => {
    const outcome = openApp();
    trackEvent('cta_click', {
      trackingId,
      platform: platform.iOS
        ? 'ios'
        : platform.android
          ? 'android'
          : 'desktop',
      inAppBrowser: platform.inAppBrowser,
      outcome,
    });
  };

  const handleInstall = () => {
    const outcome = openInstall();
    trackEvent('install_click', {
      trackingId,
      platform: platform.iOS
        ? 'ios'
        : platform.android
          ? 'android'
          : 'desktop',
      outcome,
    });
  };

  const ctaCluster = (
    <div className="flex flex-col items-center gap-3">
      <PrimaryCTA label={ctaLabel} onClick={handlePrimary} />
      <SecondaryCTA onClick={handleInstall} />
    </div>
  );

  return (
    <Layout
      desktopAside={
        <DesktopBrandPanel
          currentUrl={currentUrl}
          appStoreUrl={APP_STORE_URL}
          playStoreUrl={PLAY_STORE_URL}
          onAppStoreClick={handleInstall}
          onPlayStoreClick={handleInstall}
        />
      }
      mobileStickyCta={ctaCluster}
      desktopFlowCta={ctaCluster}
    >
      <div style={{ animation: 'var(--animate-fade-in)' }}>
        <BrandHeader />
      </div>

      <div
        className="mt-8 flex justify-center lg:mt-0 lg:justify-start"
        style={{ animation: 'var(--animate-fade-up)', animationDelay: '60ms' }}
      >
        <AlertBadge />
      </div>

      <div
        className="mt-4"
        style={{ animation: 'var(--animate-fade-up)', animationDelay: '140ms' }}
      >
        <PersonalizedMessage state={personalization} />
      </div>
    </Layout>
  );
}

export default App;
